import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import tinyGraph from "../assets/tinyGraph.svg";
import Settings from "./Settings";
import SettingsMenu from "./SettingsMenu";
import FeedbackModal from "./FeedbackModal";

const Sidebar = ({
  guestPreview = false,
  onGuestRequireAuth,
}) => {
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const closeSettings = () => setIsSettingsOpen(false);

  // Centralized Menus definition
  const Menus = [
    { title: "Discover", icon: "explore", path: "/recommendations" },
    { title: "Search", icon: "search", path: "/search" },
    { title: "Saved", icon: "bookmark", path: "/saved" },
  ];

  const openSettingsModal = () => setIsSettingsOpen(true);

  const handleGuestBlockedNav = (e, path) => {
    if (guestPreview && path !== "/recommendations") {
      e.preventDefault();
      if (onGuestRequireAuth) onGuestRequireAuth();
    }
  };

  const handleSettingsTriggerClick = () => {
    if (guestPreview && onGuestRequireAuth) {
      onGuestRequireAuth();
    } else {
      setIsSettingsMenuOpen((open) => !open);
    }
  };

  return (
    <>
      <Settings isOpen={isSettingsOpen} closeSettings={closeSettings} />
      <FeedbackModal
        open={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />
      <SettingsMenu
        open={isSettingsMenuOpen}
        onClose={() => setIsSettingsMenuOpen(false)}
        onOpenSettingsModal={openSettingsModal}
        onOpenFeedbackModal={() => setIsFeedbackOpen(true)}
      />

      {/* Mobile top nav (<550px) */}
      <aside className="nav:hidden sticky top-0 z-30 w-full bg-[#FFFFFF] shadow-sm">
        <nav className="grid h-16 grid-cols-[1fr_auto_1fr] items-center px-3">
          <div className="flex min-w-0 items-center justify-start">
            <Link to="/" className="flex items-center" title="Home">
              <img src={tinyGraph} alt="Grad Connect home" className="h-10 w-10 cursor-pointer" />
            </Link>
          </div>

          <div className="flex h-full items-center justify-center">
            <ul className="flex items-center justify-center gap-3 min-[400px]:gap-4">
              {Menus.map((menu, index) => (
                <li key={index}>
                  <NavLink
                    to={menu.path}
                    end
                    onClick={(e) => handleGuestBlockedNav(e, menu.path)}
                    className={({ isActive }) =>
                      `inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[#202531] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0196BF] focus-visible:ring-offset-2 ${
                        isActive
                          ? "bg-gray-50 hover:bg-gray-100"
                          : "hover:bg-[#F3F4F6]"
                      }`
                    }
                    aria-label={menu.title}
                    title={menu.title}
                  >
                    <span className="material-icons text-3xl">{menu.icon}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex min-w-0 items-center justify-end">
            <button
              type="button"
              data-settings-menu-trigger
              onClick={handleSettingsTriggerClick}
              className="flex items-center justify-center rounded-md p-2 text-[#202531] hover:bg-[#F3F4F6]"
              aria-label="Open account menu"
              aria-expanded={isSettingsMenuOpen}
              aria-haspopup="menu"
              title="Account menu"
            >
              <span className="material-icons text-3xl">settings</span>
            </button>
          </div>
        </nav>
      </aside>

      {/* Desktop/tablet left sidebar (>=550px) */}
      <aside className="hidden nav:block h-screen">
        <nav className="h-full flex flex-col bg-[#FFFFFF] shadow-sm" style={{ width: "80px" }}>
          {/* Logo Placeholder */}
          <div className="flex items-center justify-center p-5">
            <Link to="/" title="Home">
              <img src={tinyGraph} alt="Grad Connect home" className="w-16 h-16 cursor-pointer" />
            </Link>
          </div>

          {/* Menu Items */}
          <div className="flex-1 px-3">
            <ul className="space-y-6">
              {Menus.map((menu, index) => (
                <li
                  key={index}
                  className={`
                    relative flex items-center justify-center py-4
                    font-medium rounded-md cursor-pointer
                    transition-colors group
                    hover:bg-[#F3F4F6] text-[#202531]
                  `}
                >
                  <Link
                    to={menu.path}
                    onClick={(e) => handleGuestBlockedNav(e, menu.path)}
                    className="flex items-center justify-center w-full"
                  >
                    <span className="material-icons text-3xl">{menu.icon}</span>
                    <span
                      className={`
                        absolute left-full rounded-md px-2 py-1 ml-6
                        bg-[#F3F4F6] text-[#202531] text-sm
                        invisible opacity-0 translate-x-3 transition-all
                        group-hover:visible group-hover:opacity-100 group-hover:translate-x-0
                        z-10
                      `}
                    >
                      {menu.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Settings at the Bottom — opens account menu (popover) */}
          <div className="p-5">
            <button
              type="button"
              data-settings-menu-trigger
              onClick={handleSettingsTriggerClick}
              aria-label="Open account menu"
              aria-expanded={isSettingsMenuOpen}
              aria-haspopup="menu"
              className={`
                relative flex w-full items-center justify-center rounded-md py-4
                font-medium cursor-pointer
                transition-colors group text-[#202531]
                hover:bg-[#F3F4F6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0196BF] focus-visible:ring-offset-2
              `}
            >
              <span className="material-icons text-3xl">settings</span>
              <span
                className={`
                  absolute left-full rounded-md px-2 py-1 ml-6
                  bg-[#F3F4F6] text-[#202531] text-sm
                  invisible opacity-0 translate-x-3 transition-all
                  group-hover:visible group-hover:opacity-100 group-hover:translate-x-0
                  z-10
                `}
              >
                Settings
              </span>
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
